/**
 * LoadingPage - Minimal Loading State (#748)
 *
 * "Boring on purpose" per Frank's guidance:
 * - Muted text, not primary
 * - Instrument panel, not billboard
 *
 * Loading indicator (#2675): the branded OroLoader (animated brand-mark dots),
 * rendered inline + decorative so the outer status region owns the announcement.
 *
 * Accessibility (#750):
 * - aria-live="polite" for screen reader announcements
 * - role="status" to indicate live region
 * - prefers-reduced-motion: OroLoader degrades to the static brand mark
 *
 * 3-Layer CSS (Nightfall):
 * - text-text-muted → --text-muted → --nightfall-text-muted
 */

import { useEffect, useState } from 'react';
import { LOADING_MESSAGES } from '../config/loadingMessages';
import { OroLoader } from '../components';

interface LoadingPageProps {
  message?: string; // Override random message with specific text
  timeout?: number;
  onTimeout?: () => void;
}

export default function LoadingPage({
  message,
  timeout,
  onTimeout
}: LoadingPageProps) {
  // Pick random message ONCE on mount (stable across renders)
  const [randomMessage] = useState(
    () => LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]
  );

  // Timeout handler
  useEffect(() => {
    if (!timeout) return;

    const timer = setTimeout(() => {
      onTimeout?.();
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout, onTimeout]);

  // Use provided message or random message
  const displayMessage = message ?? randomMessage;

  return (
    <div
      className="min-h-screen bg-oro-bg flex items-center justify-center p-4 pt-8"
      role="status"
      aria-live="polite"
    >
      {/* Single line: message + branded loader, calm and minimal */}
      <p className="text-sm text-text-muted inline-flex items-center">
        {displayMessage}
        <OroLoader variant="inline" decorative className="ml-2" />
      </p>
    </div>
  );
}
